/**
 * Game Coder page.
 *
 * Layout: a compact top strip (Muse orb + the plan + the Play/Stop and Build
 * controls), then the version rail beside the game, which takes the rest of the
 * screen. There is deliberately no chat panel — the kid talks to Muse and watches
 * the plan fill in; a transcript is developer-facing noise on a child's screen.
 *
 * The build/voice interplay: pressing Build stops Muse (the agent takes ~60-90s
 * and a live Sonic stream would idle-timeout mid-build anyway), and `coderStore`
 * restarts her on completion to announce that the game is ready.
 *
 * Versions: each successful build closes off a version and empties the plan, so
 * "What to add next" is always what the child has asked for since the last build.
 * Building while an older version is selected destroys the versions after it, so
 * that path goes through a confirm — the only un-undoable action on the page.
 */

import React, { useEffect, useState } from "react";
import { View, Text, Pressable, ScrollView, StyleSheet } from "react-native";
import type { CoderNote } from "@amzn/a4k-creator-studio-shared";
import { colors, textStyle } from "../styles";
import { useNavigation } from "../navigation/useNavigation";
import { useCoderStore } from "./coderStore";
import CoderOrb from "./CoderOrb";
import GameView from "./GameView";
import VersionBar from "./VersionBar";

/** Accent per idea category, so the spec list is scannable at a glance. */
const CATEGORY_COLOR: Record<string, string> = {
  character: colors.character,
  power: colors.accent,
  level: colors.scene,
  enemy: colors.danger,
  goal: colors.beat,
  rule: colors.textSecondary,
  look: colors.character,
  sound: colors.scene,
  idea: colors.textMuted,
};

export default function CoderPage() {
  const { goBack } = useNavigation();
  const notes = useCoderStore((s) => s.notes);
  const notesAreInherited = useCoderStore((s) => s.notesAreInherited);
  const versions = useCoderStore((s) => s.versions);
  const currentVersionIndex = useCoderStore((s) => s.currentVersionIndex);
  const pendingTruncateCount = useCoderStore((s) => s.pendingTruncateCount);
  const isVoiceActive = useCoderStore((s) => s.isVoiceActive);
  const isThinking = useCoderStore((s) => s.isThinking);
  const isPlaying = useCoderStore((s) => s.isPlaying);
  const buildStatus = useCoderStore((s) => s.buildStatus);
  const buildSummary = useCoderStore((s) => s.buildSummary);
  const error = useCoderStore((s) => s.error);

  useEffect(() => {
    // Loads the notes, the current game version, and the version history, so the
    // iframe and the rail are both correct on first paint.
    useCoderStore.getState().listNotes().catch(() => {});
    return () => {
      useCoderStore.getState().stopVoice();
      useCoderStore.getState().reset();
    };
  }, []);

  const building = buildStatus === "building";
  // How many versions a build would delete, for the button label.
  const newerCount = Math.max(0, versions.length - 1 - currentVersionIndex);
  // Build only does something when there are ideas that are NOT already in the
  // game. On a loaded version the plan describes what that version already has,
  // so there is nothing to build until the child asks for something new.
  const canBuild = !building && notes.length > 0 && !notesAreInherited;
  // Kept to one line each: the label sits in a fixed-height strip below the orb, and the
  // old "Building — Muse is waiting" wrapped to two lines and was clipped.
  const museStatus = building
    ? "Building..."
    : !isVoiceActive
      ? "Tap Muse to talk"
      : isThinking
        ? "Thinking..."
        : "Listening";

  return (
    <View style={styles.root}>
      {/* ── Top strip: orb, spec, controls ───────────────────────────────── */}
      <View style={styles.top}>
        <Pressable style={styles.backBtn} onPress={goBack} testID="coder-back">
          <Text style={styles.backBtnText}>{"✕"}</Text>
        </Pressable>

        <View style={styles.orbCol}>
          <CoderOrb size={92} />
          <Text style={styles.museStatus} testID="coder-status">
            {museStatus}
          </Text>
        </View>

        <View style={styles.specCol}>
          <View style={styles.specHeader}>
            <Text style={styles.specTitle} testID="coder-notes-count">
              {notesAreInherited
                ? `What was added in this version (${notes.length})`
                : `What to add next (${notes.length})`}
            </Text>
          </View>
          <ScrollView style={styles.specList} contentContainerStyle={styles.specListContent}>
            {notes.length === 0 ? (
              <Text style={styles.specEmpty} testID="coder-notes-empty">
                Tell Muse what to add to your game. Your ideas show up here, then
                press Build to make a new version.
              </Text>
            ) : (
              <>
                {/* Build is disabled here, so say why — otherwise it reads as
                    broken rather than as "nothing to do yet". */}
                {notesAreInherited ? (
                  <Text style={styles.specHint} testID="coder-notes-hint">
                    These are already in this version. Tell Muse what to change to
                    make a new one.
                  </Text>
                ) : null}
                {notes.map((n) => (
                  <SpecRow key={n.noteId} note={n} />
                ))}
              </>
            )}
          </ScrollView>
        </View>

        <View style={styles.controls}>
          <Pressable
            style={[styles.btn, styles.playBtn, building && styles.btnDisabled]}
            disabled={building}
            onPress={() => useCoderStore.getState().setPlaying(!isPlaying)}
            testID="coder-play"
          >
            <Text style={styles.btnText}>{isPlaying ? "■  Stop" : "▶  Play"}</Text>
          </Pressable>

          <Pressable
            style={[styles.btn, styles.buildBtn, !canBuild && styles.btnDisabled]}
            disabled={!canBuild}
            onPress={() => useCoderStore.getState().requestBuild()}
            testID="coder-build"
          >
            <Text style={styles.btnText}>{building ? "Building..." : "⚒  Build"}</Text>
            {canBuild && newerCount > 0 ? (
              <Text style={styles.btnSub}>replaces {newerCount} newer</Text>
            ) : null}
          </Pressable>

          <Pressable
            style={[styles.resetBtn, building && styles.btnDisabled]}
            disabled={building}
            onPress={() => useCoderStore.getState().resetGame()}
            testID="coder-reset-game"
          >
            <Text style={styles.resetBtnText}>Starter game</Text>
          </Pressable>
        </View>
      </View>

      {/* Last build result / errors — one line, dismissible. */}
      {error ? (
        <Pressable
          style={styles.errorBar}
          onPress={() => useCoderStore.getState().dismissError()}
          testID="coder-error"
        >
          <Text style={styles.errorText} numberOfLines={2}>
            {error} (tap to dismiss)
          </Text>
        </Pressable>
      ) : buildSummary && buildStatus === "ready" ? (
        <View style={styles.summaryBar}>
          <Text style={styles.summaryText} numberOfLines={1}>
            {"✓  "}
            {buildSummary}
          </Text>
        </View>
      ) : null}

      {/* ── History rail + the game ──────────────────────────────────────── */}
      <View style={styles.gameWrap}>
        <VersionBar />
        <View style={styles.gameBox}>
          <GameView />
        </View>
      </View>

      {/* Truncation confirm. Modal-ish overlay rather than window.confirm so it
          matches the app and works the same on web and native. */}
      {pendingTruncateCount > 0 ? (
        <View style={styles.confirmBackdrop}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>
              {"Replace "}
              {pendingTruncateCount}
              {pendingTruncateCount === 1 ? " newer version?" : " newer versions?"}
            </Text>
            <Text style={styles.confirmBody}>
              You went back to an earlier game. Building from here removes the
              {pendingTruncateCount === 1 ? " version " : " versions "}
              you made after it.
            </Text>
            <View style={styles.confirmBtns}>
              <Pressable
                style={[styles.confirmBtn, styles.confirmCancel]}
                onPress={() => useCoderStore.getState().cancelBuild()}
                testID="coder-build-cancel"
              >
                <Text style={styles.confirmCancelText}>No, go back</Text>
              </Pressable>
              <Pressable
                style={[styles.confirmBtn, styles.confirmGo]}
                onPress={() => useCoderStore.getState().buildGame().catch(() => {})}
                testID="coder-build-confirm"
              >
                <Text style={styles.confirmGoText}>Yes, build it</Text>
              </Pressable>
            </View>
          </View>
        </View>
      ) : null}
    </View>
  );
}

/**
 * One idea in the plan, expandable to show its detail.
 *
 * The title is a 2-5 word label ("Scared hands-up reaction"); the real detail lives
 * in the body ("When a shape is about to squish a character, right above its head,
 * it lifts its hands and waves them like it's scared"). Only the title was ever
 * rendered, which made precise requests look like they had been flattened into a
 * vague summary — the detail was captured and built, just invisible.
 *
 * Collapsed by default: the strip is ~150px tall and the child mostly wants to see
 * that their idea landed, not re-read it. The + is there for when they doubt it.
 */
function SpecRow({ note }: { note: CoderNote }) {
  const accent = CATEGORY_COLOR[note.category] ?? colors.textMuted;
  const [expanded, setExpanded] = useState(false);
  // Nothing to reveal when the body just restates the title.
  const hasDetail = note.body.trim().length > 0 && note.body.trim() !== note.title.trim();

  return (
    <View testID="coder-note">
      <View style={styles.specRow}>
        <View style={[styles.specDot, { backgroundColor: accent }]} />
        {hasDetail ? (
          <Pressable
            onPress={() => setExpanded((e) => !e)}
            style={styles.specExpand}
            testID="coder-note-expand"
            accessibilityLabel={
              expanded ? `Hide details for ${note.title}` : `Show details for ${note.title}`
            }
          >
            <Text style={styles.specExpandText}>{expanded ? "−" : "+"}</Text>
          </Pressable>
        ) : (
          // Keeps titles aligned whether or not a row has a toggle.
          <View style={styles.specExpand} />
        )}
        <Pressable
          style={styles.specTitlePress}
          onPress={hasDetail ? () => setExpanded((e) => !e) : undefined}
          disabled={!hasDetail}
        >
          <Text style={styles.specRowTitle} numberOfLines={1}>
            {note.title}
          </Text>
        </Pressable>
        <Pressable
          onPress={() => useCoderStore.getState().deleteNote(note.noteId)}
          style={styles.specDelete}
          accessibilityLabel={`Remove ${note.title}`}
        >
          <Text style={styles.specDeleteText}>{"✕"}</Text>
        </Pressable>
      </View>
      {expanded && hasDetail ? (
        <Text style={styles.specBody} testID="coder-note-body">
          {note.body}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  // Same cream as the template-selection screen, so moving from the picker into the
  // builder does not change the page colour under the child.
  root: { flex: 1, backgroundColor: colors.gameLabCream, padding: 14, gap: 10 },

  /*
   * 168, up from 150.
   *
   * The orb is 92px but its art is drawn from a 2x iframe, so the visible ball ends
   * ~127px down the strip; the status label then has to sit below that and was being
   * clipped by the old height. Raising the strip is preferable to shrinking the orb (it
   * is the focal point of the page) or tucking the label under the glow (unreadable).
   */
  top: { flexDirection: "row", alignItems: "stretch", gap: 14, height: 168 },
  backBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
  },
  backBtnText: { fontSize: 15, color: colors.text },

  orbCol: { alignItems: "center", justifyContent: "center", width: 120 },
  museStatus: {
    fontSize: 12,
    color: colors.textSecondary,
    ...textStyle.body,
    textAlign: "center",
    /*
     * Pushed clear of the orb's glow.
     *
     * CoderOrb declares a `size`-square box but renders its iframe at 2x that, centred
     * with a negative offset, because the glow overflows the logical bounds (see
     * CoderOrb.tsx). At size 92 that means ~46px of translucent glow spilling below the
     * box — which landed on top of this label. Sitting it below the spill keeps the text
     * readable without shrinking the orb.
     */
    marginTop: 26,
  },

  specCol: {
    flex: 1,
    borderRadius: 12,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: "hidden",
  },
  specHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  specTitle: { fontSize: 15, ...textStyle.heading, color: colors.text },
  specList: { flex: 1 },
  specListContent: { paddingVertical: 4 },
  specHint: {
    fontSize: 12,
    color: colors.textMuted,
    ...textStyle.body,
    paddingHorizontal: 12,
    paddingTop: 4,
    paddingBottom: 6,
    lineHeight: 16,
  },
  specEmpty: {
    fontSize: 13,
    color: colors.textMuted,
    ...textStyle.body,
    padding: 12,
    lineHeight: 18,
  },
  specRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  specDot: { width: 8, height: 8, borderRadius: 4 },
  specExpand: { width: 16, alignItems: "center" },
  specExpandText: { fontSize: 14, color: colors.textMuted, ...textStyle.heading, lineHeight: 16 },
  specTitlePress: { flex: 1 },
  specBody: {
    fontSize: 12,
    color: colors.textSecondary,
    ...textStyle.body,
    // Indented under the title, clear of the dot and the toggle.
    paddingLeft: 44,
    paddingRight: 26,
    paddingBottom: 6,
    lineHeight: 16,
  },
  specRowTitle: { fontSize: 13, color: colors.text, ...textStyle.body },
  specDelete: { padding: 2 },
  specDeleteText: { fontSize: 11, color: colors.textMuted },

  controls: { width: 150, gap: 8, justifyContent: "center" },
  btn: { paddingVertical: 12, borderRadius: 10, alignItems: "center" },
  playBtn: { backgroundColor: colors.primary },
  buildBtn: { backgroundColor: colors.accent },
  btnDisabled: { opacity: 0.45 },
  btnText: { color: colors.white, fontSize: 15, ...textStyle.heading },
  btnSub: { color: colors.white, fontSize: 10, ...textStyle.body, opacity: 0.85, marginTop: 1 },
  resetBtn: { paddingVertical: 6, alignItems: "center" },
  resetBtnText: { color: colors.textMuted, fontSize: 12, ...textStyle.body },

  errorBar: { padding: 9, borderRadius: 8, backgroundColor: colors.danger },
  errorText: { color: colors.white, fontSize: 13, ...textStyle.body },
  summaryBar: { padding: 9, borderRadius: 8, backgroundColor: colors.surfaceHover },
  summaryText: { color: colors.textSecondary, fontSize: 13, ...textStyle.body },

  gameWrap: { flex: 1, flexDirection: "row", gap: 10 },
  gameBox: { flex: 1 },

  confirmBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(10,11,22,0.78)",
  },
  confirmCard: {
    maxWidth: 380,
    padding: 22,
    borderRadius: 16,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    gap: 10,
  },
  confirmTitle: { fontSize: 19, color: colors.text, ...textStyle.heading },
  confirmBody: { fontSize: 14, color: colors.textSecondary, ...textStyle.body, lineHeight: 20 },
  confirmBtns: { flexDirection: "row", gap: 10, marginTop: 6 },
  confirmBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, alignItems: "center" },
  confirmCancel: { backgroundColor: colors.surfaceHover },
  confirmCancelText: { color: colors.textSecondary, fontSize: 14, ...textStyle.heading },
  confirmGo: { backgroundColor: colors.accent },
  confirmGoText: { color: colors.white, fontSize: 14, ...textStyle.heading },
});
